/**
 * File sync thunk for synchronizing API-fetched files with local Helia storage.
 *
 * This thunk orchestrates the workflow of:
 * 1. Receiving the already-fetched file list from the caller
 * 2. Checking local Helia storage for each file
 * 3. Adding existing files to heliaSlice
 * 4. Queueing missing files for background download via the clone queue
 */
import { CID } from 'multiformats/cid';

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { mapSixFilesToFileMetadata } from '@/store/mappers/mapSixFileToFileMetadata';
import { addFileMetadata } from '@/store/slices/poc/heliaSlice';
import { enqueueFile } from '@/store/slices/poc/cloneQueueSlice';
import { processCloneQueue } from '@/store/thunks/poc/cloneQueue/processCloneQueue';
import type { SixFile } from '@/types/six/Six';

/**
 * Parse a CID string to a CID object.
 * @param cidString - CID as string
 * @returns CID object
 */
function parseCid(cidString: string): CID {
  return CID.parse(cidString);
}

/**
 * Synchronizes files from the API with local Helia storage.
 *
 * For each file passed in:
 * - If file exists locally in Helia: adds metadata to heliaSlice
 * - If file is missing locally: queues it for background download
 *
 * @param sixId - The Six ID to sync files for
 * @param files - The SixFile[] to sync (passed directly to avoid reading stale Redux state)
 */
export const syncFilesFromApi = createAppAsyncThunk(
  'sixDetail/syncFiles',
  async(
    { sixId, files }: { sixId: string; files: SixFile[] },
    { dispatch, extra: { pocServices, loggerService } }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:six:detail' });

    if (files.length === 0) {
      return { sixId, synced: 0, missing: 0 };
    }

    // Map SixFile[] to FileMetadata[]
    const fileMetadataList = mapSixFilesToFileMetadata(files, sixId);

    const { p2pStackManagerService } = pocServices;
    const heliaNode = p2pStackManagerService.getHeliaNode(sixId);

    // If Helia node isn't ready, skip local sync but still return the mapped files
    if (heliaNode === null) {
      log.warn(`Helia node not ready for sixId: ${sixId}, skipping local sync`);
      return { sixId, synced: 0, missing: 0, skipped: fileMetadataList.length };
    }

    let synced = 0;
    let missing = 0;

    for (const metadata of fileMetadataList) {
      try {
        // Use pins.isPinned() to determine if file is fully local.
        // A pinned file has all DAG blocks present in the local blockstore.
        // fs.stat() is unreliable for dag-pb CIDs — it succeeds with only the
        // root block present, before child blocks have been fetched via Bitswap.
        const isPinned = await heliaNode.helia.pins.isPinned(parseCid(metadata.cid));

        if (isPinned) {
          dispatch(addFileMetadata(metadata));
          synced++;
        } else {
          dispatch(enqueueFile({
            sixId,
            cid: metadata.cid,
            metadata,
            size: metadata.size,
            priority: 'normal',
          }));
          missing++;
        }
      } catch (error) {
        log.error(`Error checking file ${metadata.cid}:`, error instanceof Error ? error : String(error));
      }
    }

    // Start background download if there are missing files
    if (missing > 0) {
      void dispatch(processCloneQueue());
    }

    return { sixId, synced, missing };
  }
);
